import * as Immutable from 'immutable';
import * as t from 'io-ts';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import SelectedAlphasMain from '../../components/SelectedAlphas/SelectedAlphasMain';
import { StateType } from '../../reducers';
import { AlphasDataResults } from '../../reducers/AlphasReducer/AlphasReducer.types';
import { alphasOptionsSelector } from '../../reducers/AlphasReducer/AlphasSelector';
import {
  codeFromselectedAlphasSettingsSelector,
  delayFromselectedAlphasSettingsSelector,
  instrumentTypeFromselectedAlphasSettingsSelector,
  regionFromselectedAlphasSettingsSelector,
  selectionHandlingFromSelectedAlphasSettingsSelector,
  selectionLimitFromSelectedAlphasSettingsSelector,
} from '../../reducers/GenericSelectors';
import { ALPHAS_SUPER_SELECTION } from '../../shared/constants';
import { alphaColumnsSelectedAlphas } from '../../shared/data/AlphaDetailsSummaryColumns';
import { intlFormatMessage, localiseValue } from '../../shared/helpers';
import { formatHTMLMessageHelper, FormattedMessageHelper } from '../../shared/helpers/internationalization';

interface SelectedAlphasProps {
  path: string[];
  id: string;
  alphaSelectionsPath?: string;
  fetchingAlphaSelectionsPath?: string;
}

export const SelectedAlphas = (props: SelectedAlphasProps) => {
  const { path, id, alphaSelectionsPath = 'alphaSelections', fetchingAlphaSelectionsPath = 'fetchingAlphaSelections' } = props;
  const intl = useIntl();

  // @ts-ignore
  const fetchingAlphaSelections: boolean = useSelector((state: StateType) => state.getIn(path.concat([fetchingAlphaSelectionsPath])));
  // @ts-ignore
  const alphaSelections: Immutable.List<t.TypeOf<typeof AlphasDataResults>> = useSelector((state: StateType) =>
    state.getIn(path.concat([alphaSelectionsPath, 'results']))
  );
  // @ts-ignore
  const numAlphasSelected: number = useSelector((state: StateType) => state.getIn(path.concat([alphaSelectionsPath, 'count'])));
  // @ts-ignore
  const alphaSelectionMessage: string = useSelector((state: StateType) => state.getIn(path.concat([alphaSelectionsPath, 'message'])));
  // @ts-ignore
  const alphaId = useSelector((state: StateType) => state.getIn(path.concat(['alphaDetail', 'id'])));
  // @ts-ignore
  const alphasOptions: Immutable.List<any> = useSelector(alphasOptionsSelector);
  const selectionCode = useSelector((state: StateType) => codeFromselectedAlphasSettingsSelector(state, path));
  const region = useSelector((state: StateType) => regionFromselectedAlphasSettingsSelector(state, path));
  const delay = useSelector((state: StateType) => delayFromselectedAlphasSettingsSelector(state, path));
  const instrumentType = useSelector((state: StateType) => instrumentTypeFromselectedAlphasSettingsSelector(state, path));
  const selectionLimit = useSelector((state: StateType) => selectionLimitFromSelectedAlphasSettingsSelector(state, path));
  const selectionHandling = useSelector((state: StateType) => selectionHandlingFromSelectedAlphasSettingsSelector(state, path));

  const getSettingsTableHeader = () =>
    alphaColumnsSelectedAlphas.map((column: any) => ({
      title: intlFormatMessage(column.title, intl),
      id: column.id,
      type: column.type,
    }));

  const getSettingsTableValues = () => {
    const ids = getSettingsTableHeader();

    return (
      alphaSelections &&
      alphaSelections.map((alpha: any) =>
        ids.map(column => {
          const id = column.id;
          if (id === 'name') {
            return alpha.get(id) ? (
              <Link className="link__blue" to={`/alpha/${alpha.get('id')}`} target="_blank">
                {alpha.get(id)}
              </Link>
            ) : (
              <Link className="link__blue" to={`/alpha/${alpha.get('id')}`} target="_blank">
                anonymous
              </Link>
            );
          } else if (alpha.get(id) !== undefined) {
            return localiseValue(alpha.get(id), column.type);
          } else if (alpha.getIn(['settings', id]) !== undefined) {
            return localiseValue(alpha.getIn(['settings', id]), column.type);
          }

          return localiseValue(alpha.getIn(['is', id]), column.type);
        })
      )
    );
  };

  const getStagePerformanceColumnsTypes = () => {
    const tableHeader = getSettingsTableHeader();
    const ids = tableHeader.map((header: any) => header.id);

    return ids.map((id, index) => {
      if (alphasOptions && alphasOptions.get(id) !== undefined) {
        return alphasOptions && alphasOptions.getIn([id, 'type']);
      } else if (alphasOptions && alphasOptions.getIn([id, 'settings', 'children', 'type'])) {
        return alphasOptions.getIn([id, 'settings', 'children', 'type']);
      } else {
        return tableHeader[index].type;
      }
    });
  };

  const settingsValues = getSettingsTableValues();
  const encodeSelection = selectionCode ? encodeURIComponent(selectionCode) : '';
  const linkToAlphas = alphaId
    ? `/alphas/${alphaId}/alphas`
    : `${ALPHAS_SUPER_SELECTION}?` +
      `settings.region=${encodeURIComponent(region)}&` +
      `settings.delay=${encodeURIComponent(delay)}&` +
      `settings.instrumentType=${encodeURIComponent(instrumentType)}&` +
      `selectionLimit=${encodeURIComponent(selectionLimit)}&` +
      `selectionHandling=${encodeURIComponent(selectionHandling)}&` +
      `selection=${encodeSelection}`;

  return (
    <div id={id} className="alphas-details-content__box">
      <div className="selected-alphas__title">
        <FormattedMessageHelper id="alphasDetails.title.selected.alphas" />
        {numAlphasSelected > 0 && (
          <div className="selected-alphas__left">
            <div>
              <p>{formatHTMLMessageHelper(intl, 'alphasDetails.title.num.selected.alphas', { numAlphasSelected })}</p>
              {alphaSelectionMessage && (
                <p>
                  <strong>{alphaSelectionMessage}.</strong>
                </p>
              )}
            </div>
            <Link to={linkToAlphas} className="learn__header-link">
              <p>
                <FormattedMessageHelper id="alphasDetails.title.view.all" />
              </p>
            </Link>
          </div>
        )}
      </div>
      <SelectedAlphasMain
        settingsTableHeader={getSettingsTableHeader()}
        settingsTableValues={settingsValues && numAlphasSelected > 0 && settingsValues.toJS()}
        stagePerformanceColumnsTypes={getStagePerformanceColumnsTypes()}
        isLoading={fetchingAlphaSelections}
      />
    </div>
  );
};

export default SelectedAlphas;
